'use client'

import { useSearchPickerContext } from '@trustoo/components/searchPicker/SearchPickerContext'
import clsx from 'clsx'
import { ComponentPropsWithoutRef, KeyboardEvent, useCallback, useEffect, useRef } from 'react'
import { useEventListener } from 'usehooks-ts'

/**
 * SearchPicker.DropdownRoot - External Event Handler & Conditional Renderer
 *
 * **Role in Compound Structure:**
 * This component handles external interactions like clicks outside and window resizing.
 * It only renders when the picker is open, following a conditional rendering pattern.
 * It follows the **Single Responsibility Principle** by handling only event logic,
 * not UI concerns.
 *
 * **Responsibilities:**
 * - Conditionally renders dropdown content only when the picker is open
 * - Handles click-outside detection to close the dropdown
 * - Handles window resize events (closes on width change, not height for mobile keyboard)
 * - Applies maxHeight from context to dropdown container
 * - Handles keyboard interactions (Escape to close)
 */
const SearchPickerDropdownRoot = ({
    children,
    onKeyDown,
    className,
    ...rest
}: ComponentPropsWithoutRef<'div'>) => {
    const { isOpen, closePicker, maxHeight, contentRef } = useSearchPickerContext()
    const dropdownRef = useRef<HTMLDivElement>(null)
    const lastWindowWidth = useRef<number>(0)

    const handleKeyDown = useCallback(
        (e: KeyboardEvent<HTMLDivElement>) => {
            if (e.key === 'Escape') {
                e.preventDefault()
                e.stopPropagation()
                closePicker()
            } else {
                onKeyDown?.(e)
            }
        },
        [closePicker, onKeyDown],
    )

    // Handle resize - close on width change (not height to avoid mobile keyboard issues)
    const onResize = useCallback(() => {
        if (!isOpen) return

        const currentWindowWidth = window.innerWidth

        // Only close the dropdown if the window width is changed
        if (currentWindowWidth !== lastWindowWidth.current) {
            lastWindowWidth.current = currentWindowWidth
            closePicker()
        }
    }, [isOpen, closePicker])

    // Handle click outside to close
    const onBodyClick = useCallback(
        (e: Event) => {
            // Prefer the content wrapper (input + dropdown) as the boundary for the outside click,
            // fall back to the dropdown ref alone when Content is not used
            const boundary = contentRef.current ?? dropdownRef.current

            if (boundary && !boundary.contains(e.target as Node)) {
                closePicker({ autoSelect: true })
            }
        },
        [closePicker, contentRef],
    )

    // Initialize window width
    useEffect(() => {
        lastWindowWidth.current = window.innerWidth
    }, [])

    // Register event listeners
    useEventListener('resize', onResize)
    useEffect(() => {
        if (typeof document === 'undefined' || !isOpen) return

        const handleClick = (e: Event) => onBodyClick(e)
        document.body.addEventListener('click', handleClick, { capture: true })

        return () => {
            document.body.removeEventListener('click', handleClick, { capture: true })
        }
    }, [isOpen, onBodyClick])

    if (!isOpen) {
        return null
    }

    return (
        <div
            ref={dropdownRef}
            className={clsx('flex flex-col overflow-hidden', className)}
            style={maxHeight === undefined ? undefined : { maxHeight }}
            onKeyDown={handleKeyDown}
            {...rest}>
            {children}
        </div>
    )
}

export default SearchPickerDropdownRoot
